import { isEqual } from "@common/helpers/comparison";
import {
    AuditLogChangeType,
    AuditLogDiff,
    ResourceType,
} from "@common/types/auditLog";
import {
    RuleGroupType,
    TransactionRuleAction,
    TransactionRuleCondition,
} from "@common/types/transactionRule";
import { Counterparty } from "@common/types/counterparty";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import {
    RuleVersion,
    isActionField,
    isConditionField,
} from "../views/historyRuleDetailsViewConfig";
import {
    extractRuleDataFromSnapshot,
    mergeSnapshotData,
} from "./ruleSnapshotAdapter";

type SnapshotRecord = Record<string, unknown>;

export type FieldDiffKind = "stable" | "changed" | "added" | "removed";

export interface FieldDiffItem {
    field: string;
    kind: FieldDiffKind;
    before: unknown;
    after: unknown;
}

export type StatusColumn =
    | { kind: "single"; status: "active" | "disabled" }
    | {
          kind: "diff";
          beforeStatus: "active" | "disabled";
          afterStatus: "active" | "disabled";
      };

export type GroupColumn =
    | { kind: "name"; label: string }
    | { kind: "diff"; beforeLabel: string; afterLabel: string };

export type RuleItemSegment<T> =
    | { kind: "stable"; items: T[] }
    | { kind: "changed"; before: T; after: T }
    | { kind: "added"; items: T[] }
    | { kind: "removed"; items: T[] };

export interface RuleDetailsRowData {
    id: string;
    versionId: string;
    ruleId: string;
    changeType: AuditLogChangeType;
    ruleType: RuleGroupType;
    groupColumn: GroupColumn;
    statusColumn: StatusColumn;
    logDate: Date;
    conditions: TransactionRuleCondition[];
    conditionTexts: string[];
    actions: TransactionRuleAction[];
    actionTexts: string[];
    prevConditions: TransactionRuleCondition[];
    prevActions: TransactionRuleAction[];
    prevConditionTexts: string[];
    prevActionTexts: string[];
    conditionSegments: Array<RuleItemSegment<TransactionRuleCondition>> | null;
    actionSegments: Array<RuleItemSegment<TransactionRuleAction>> | null;
    conditionFieldDiffs: FieldDiffItem[];
    actionFieldDiffs: FieldDiffItem[];
    counterparties: Counterparty[];
    normalizedDescriptions: NormalizedDescriptionInfo[];
    resource?: Record<string, unknown>;
}

function toArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

function castConditions(items: unknown[]): TransactionRuleCondition[] {
    return items
        .filter(
            (item): item is Record<string, unknown> =>
                !!item && typeof item === "object" && "conditionType" in item,
        )
        .map((c, i) => ({
            id: String(c.id ?? `cond-${i}`),
            conditionType: c.conditionType,
            value: c.value,
            transferDirection: c.transferDirection ?? null,
        })) as TransactionRuleCondition[];
}

function castActions(items: unknown[]): TransactionRuleAction[] {
    return items
        .filter(
            (item): item is Record<string, unknown> =>
                !!item && typeof item === "object" && "actionType" in item,
        )
        .map((a, i) => ({
            id: String(a.id ?? `act-${i}`),
            actionType: a.actionType,
            value: a.value,
            transferDirection: a.transferDirection ?? null,
        })) as TransactionRuleAction[];
}

function extractTexts(items: unknown[]): string[] {
    return items.filter((item): item is string => typeof item === "string");
}

function getDiffValue(
    diff: AuditLogDiff | undefined,
    side: "before" | "after",
): unknown[] {
    if (!diff) {
        return [];
    }
    return toArray(diff[side].value);
}

function getDiff(
    diffs: AuditLogDiff[],
    field: string,
): AuditLogDiff | undefined {
    return diffs.find((d) => d.field === field);
}

function getSnapshots(version: RuleVersion): {
    before?: SnapshotRecord;
    after?: SnapshotRecord;
} {
    const m = version.metadata;
    if (!m || typeof m !== "object") {
        return {};
    }
    return {
        before: m.beforeSnapshot as SnapshotRecord | undefined,
        after: m.afterSnapshot as SnapshotRecord | undefined,
    };
}

function refLabel(ref: { value?: unknown; valueLabel?: string }): string {
    if (ref.valueLabel) {
        return ref.valueLabel;
    }
    if (ref.value === null || ref.value === undefined) {
        return "-";
    }
    return String(ref.value);
}

function readGroupNameFromRecord(value: unknown): string | null {
    if (!value || typeof value !== "object") {
        return null;
    }
    const rawName = (value as Record<string, unknown>).name;
    return typeof rawName === "string" && rawName.length > 0 ? rawName : null;
}

function readGroupTypeFromRecord(value: unknown): RuleGroupType | null {
    if (!value || typeof value !== "object") {
        return null;
    }
    const rawType = (value as Record<string, unknown>).type;
    if (
        rawType === RuleGroupType.TRANSACTION ||
        rawType === RuleGroupType.ACCOUNTING ||
        rawType === RuleGroupType.TRANSFER ||
        rawType === RuleGroupType.SPLIT
    ) {
        return rawType;
    }
    return null;
}

function resolveRuleGroupName(v: RuleVersion): string {
    const resourceGroupName = readGroupNameFromRecord(v.resource?.group);
    if (resourceGroupName) {
        return resourceGroupName;
    }

    const snapshots = getSnapshots(v);
    const preferredSnapshot =
        v.changeType === AuditLogChangeType.DELETE
            ? (snapshots.before ?? snapshots.after)
            : (snapshots.after ?? snapshots.before);
    const snapshotGroupName = readGroupNameFromRecord(preferredSnapshot?.group);
    if (snapshotGroupName) {
        return snapshotGroupName;
    }

    const groupDiff = getDiff(v.diffs, "groupId");
    if (groupDiff) {
        const preferredRef =
            v.changeType === AuditLogChangeType.DELETE
                ? groupDiff.before
                : groupDiff.after;
        const label = refLabel(preferredRef);
        if (label !== "-") {
            return label;
        }
    }

    return v.ruleGroupName;
}

function toBoolean(value: unknown): boolean | null {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string") {
        if (value.toLowerCase() === "true") {
            return true;
        }
        if (value.toLowerCase() === "false") {
            return false;
        }
    }
    return null;
}

function formatRuleStatusLabel(
    field: string,
    value: unknown,
): "active" | "disabled" {
    const parsed = toBoolean(value);
    if (parsed !== null) {
        if (field === "enabled") {
            return parsed ? "active" : "disabled";
        }
        return parsed ? "disabled" : "active";
    }

    if (typeof value === "string") {
        const normalized = value.toLowerCase();
        if (
            normalized === "disabled" ||
            normalized === "disable" ||
            normalized === "inactive"
        ) {
            return "disabled";
        }
    }

    return "active";
}

function resolveRuleStatusColumn(v: RuleVersion): StatusColumn {
    const statusDiff =
        getDiff(v.diffs, "isDisabled") ?? getDiff(v.diffs, "enabled");

    if (v.changeType !== AuditLogChangeType.UPDATE) {
        if (statusDiff) {
            const side =
                v.changeType === AuditLogChangeType.DELETE ? "before" : "after";
            return {
                kind: "single",
                status: formatRuleStatusLabel(
                    statusDiff.field,
                    statusDiff[side].value,
                ),
            };
        }

        const snapshots = getSnapshots(v);
        const snapshot =
            v.changeType === AuditLogChangeType.DELETE
                ? (snapshots.before ?? snapshots.after)
                : (snapshots.after ?? snapshots.before);
        const resourceOrSnapshot = (v.resource ?? snapshot ?? {}) as Record<
            string,
            unknown
        >;
        const resourceStatus = (resourceOrSnapshot.isDisabled ??
            resourceOrSnapshot.enabled) as unknown | undefined;
        const field =
            "isDisabled" in resourceOrSnapshot ? "isDisabled" : "enabled";

        return {
            kind: "single",
            status: formatRuleStatusLabel(field, resourceStatus),
        };
    }

    if (statusDiff) {
        const diffField = statusDiff.field;
        const beforeStatus = formatRuleStatusLabel(
            diffField,
            statusDiff.before.value,
        );
        const afterStatus = formatRuleStatusLabel(
            diffField,
            statusDiff.after.value,
        );

        if (beforeStatus === afterStatus) {
            return {
                kind: "single",
                status: afterStatus,
            };
        }

        return {
            kind: "diff",
            beforeStatus,
            afterStatus,
        };
    }

    const snapshots = getSnapshots(v);
    const snapshot = snapshots.after ?? snapshots.before;
    const resourceOrSnapshot = (v.resource ?? snapshot ?? {}) as Record<
        string,
        unknown
    >;
    const resourceStatus = (resourceOrSnapshot.isDisabled ??
        resourceOrSnapshot.enabled) as unknown | undefined;
    const field = "isDisabled" in resourceOrSnapshot ? "isDisabled" : "enabled";

    return {
        kind: "single",
        status: formatRuleStatusLabel(field, resourceStatus),
    };
}

function snapshotHasFieldKeys(
    before: SnapshotRecord | undefined,
    after: SnapshotRecord | undefined,
    predicate: (field: string) => boolean,
): boolean {
    for (const k of Object.keys(before ?? {})) {
        if (predicate(k)) {
            return true;
        }
    }
    for (const k of Object.keys(after ?? {})) {
        if (predicate(k)) {
            return true;
        }
    }
    return false;
}

function collectAllFieldItems(params: {
    snaps: { before?: SnapshotRecord; after?: SnapshotRecord };
    diffs: AuditLogDiff[];
    predicate: (field: string) => boolean;
}): FieldDiffItem[] {
    const { snaps, diffs, predicate } = params;
    const beforeSnap = snaps.before ?? {};
    const afterSnap = snaps.after ?? {};
    const changedDiffs = diffs.filter((d) => predicate(d.field));

    const allKeys = new Set<string>();
    for (const key of Object.keys(beforeSnap)) {
        if (predicate(key)) {
            allKeys.add(key);
        }
    }
    for (const key of Object.keys(afterSnap)) {
        if (predicate(key)) {
            allKeys.add(key);
        }
    }
    for (const d of changedDiffs) {
        allKeys.add(d.field);
    }

    const changedFieldSet = new Set(changedDiffs.map((d) => d.field));
    const items: FieldDiffItem[] = [];

    for (const key of [...allKeys].sort((a, b) => a.localeCompare(b))) {
        const diff = changedDiffs.find((d) => d.field === key);
        const before = diff ? diff.before.value : beforeSnap[key];
        const after = diff ? diff.after.value : afterSnap[key];

        if (changedFieldSet.has(key)) {
            if (before === null && after !== null) {
                items.push({ field: key, kind: "added", before, after });
            } else if (before !== null && after === null) {
                items.push({ field: key, kind: "removed", before, after });
            } else {
                items.push({ field: key, kind: "changed", before, after });
            }
        } else if (isEqual(before, after)) {
            items.push({ field: key, kind: "stable", before, after });
        } else if ((before === null || before === undefined) && after != null) {
            items.push({ field: key, kind: "added", before, after });
        } else if (before != null && (after === null || after === undefined)) {
            items.push({ field: key, kind: "removed", before, after });
        } else {
            items.push({ field: key, kind: "changed", before, after });
        }
    }

    return items;
}

function resolveUpdateDisplayConditions(v: RuleVersion): {
    conditions: TransactionRuleCondition[];
    conditionTexts: string[];
    prevConditionTexts: string[];
} {
    const fromResource = castConditions(toArray(v.resource?.conditions));
    if (fromResource.length > 0) {
        return {
            conditions: fromResource,
            conditionTexts: [],
            prevConditionTexts: [],
        };
    }

    const rawDiff = getDiff(v.diffs, "rawConditions");
    if (rawDiff) {
        return {
            conditions: castConditions(getDiffValue(rawDiff, "after")),
            conditionTexts: [],
            prevConditionTexts: [],
        };
    }

    const condDiff = getDiff(v.diffs, "conditions");
    if (condDiff) {
        return {
            conditions: [],
            conditionTexts: extractTexts(getDiffValue(condDiff, "after")),
            prevConditionTexts: extractTexts(getDiffValue(condDiff, "before")),
        };
    }

    return { conditions: [], conditionTexts: [], prevConditionTexts: [] };
}

function resolveUpdateDisplayActions(v: RuleVersion): {
    actions: TransactionRuleAction[];
    actionTexts: string[];
    prevActionTexts: string[];
} {
    const fromResource = castActions(toArray(v.resource?.actions));
    if (fromResource.length > 0) {
        return { actions: fromResource, actionTexts: [], prevActionTexts: [] };
    }

    const rawDiff = getDiff(v.diffs, "rawActions");
    if (rawDiff) {
        return {
            actions: castActions(getDiffValue(rawDiff, "after")),
            actionTexts: [],
            prevActionTexts: [],
        };
    }

    const actDiff = getDiff(v.diffs, "actions");
    if (actDiff) {
        return {
            actions: [],
            actionTexts: extractTexts(getDiffValue(actDiff, "after")),
            prevActionTexts: extractTexts(getDiffValue(actDiff, "before")),
        };
    }

    return { actions: [], actionTexts: [], prevActionTexts: [] };
}

function isEqualCondition(
    a: TransactionRuleCondition,
    b: TransactionRuleCondition,
): boolean {
    return (
        a.id === b.id &&
        a.conditionType === b.conditionType &&
        isEqual(a.value, b.value) &&
        (a.transferDirection ?? null) === (b.transferDirection ?? null)
    );
}

function isEqualAction(
    a: TransactionRuleAction,
    b: TransactionRuleAction,
): boolean {
    return (
        a.id === b.id &&
        a.actionType === b.actionType &&
        isEqual(a.value, b.value) &&
        (a.transferDirection ?? null) === (b.transferDirection ?? null)
    );
}

function buildRuleItemSegments<T extends { id: string }>(
    beforeList: T[],
    afterList: T[],
    isEqualItem: (a: T, b: T) => boolean,
): Array<RuleItemSegment<T>> {
    const beforeById = new Map(beforeList.map((item) => [item.id, item]));
    const afterIds = new Set(afterList.map((item) => item.id));
    const segments: Array<RuleItemSegment<T>> = [];
    let stableBuffer: T[] = [];

    const flushStable = () => {
        if (stableBuffer.length > 0) {
            segments.push({ kind: "stable", items: [...stableBuffer] });
            stableBuffer = [];
        }
    };

    for (const item of afterList) {
        const prev = beforeById.get(item.id);
        if (!prev) {
            flushStable();
            segments.push({ kind: "added", items: [item] });
            continue;
        }
        if (isEqualItem(prev, item)) {
            stableBuffer.push(item);
            continue;
        }
        flushStable();
        segments.push({ kind: "changed", before: prev, after: item });
    }
    flushStable();

    const removed = beforeList.filter((b) => !afterIds.has(b.id));
    if (removed.length > 0) {
        segments.push({ kind: "removed", items: removed });
    }

    return segments;
}

function resolveBeforeRawConditions(
    diffs: AuditLogDiff[],
    snaps: { before?: SnapshotRecord; after?: SnapshotRecord },
): TransactionRuleCondition[] {
    const rawCondDiff = getDiff(diffs, "rawConditions");
    if (!rawCondDiff) {
        return [];
    }
    return castConditions(
        snaps.before?.rawConditions
            ? toArray(snaps.before.rawConditions)
            : getDiffValue(rawCondDiff, "before"),
    );
}

function resolveBeforeRawActions(
    diffs: AuditLogDiff[],
    snaps: { before?: SnapshotRecord; after?: SnapshotRecord },
): TransactionRuleAction[] {
    const rawActDiff = getDiff(diffs, "rawActions");
    if (!rawActDiff) {
        return [];
    }
    return castActions(
        snaps.before?.rawActions
            ? toArray(snaps.before.rawActions)
            : getDiffValue(rawActDiff, "before"),
    );
}

function buildPerFieldSnapshotFromDiffs(
    diffs: AuditLogDiff[],
    side: "before" | "after",
): SnapshotRecord | undefined {
    const snapshot: SnapshotRecord = {};

    for (const diff of diffs) {
        if (!isConditionField(diff.field) && !isActionField(diff.field)) {
            continue;
        }

        const value = diff[side].value;
        if (!value || typeof value !== "object") {
            continue;
        }

        snapshot[diff.field] = value;
    }

    return Object.keys(snapshot).length > 0 ? snapshot : undefined;
}

function resolveRuleId(v: RuleVersion): string {
    const resourceId = v.resource?.id;
    if (typeof resourceId === "string" && resourceId.length > 0) {
        return resourceId;
    }
    return v.versionId;
}

function resourceTypeToRuleGroupType(
    resourceType: ResourceType | undefined,
): RuleGroupType | null {
    if (!resourceType) {
        return null;
    }
    if (resourceType === ResourceType.ACCOUNTING_RULE) {
        return RuleGroupType.ACCOUNTING;
    }
    if (resourceType === ResourceType.TRANSFER_RULE) {
        return RuleGroupType.TRANSFER;
    }
    if (resourceType === ResourceType.SPLIT_RULE) {
        return RuleGroupType.SPLIT;
    }
    if (resourceType === ResourceType.TRANSACTION_RULE) {
        return RuleGroupType.TRANSACTION;
    }
    return null;
}

function resolveRuleType(v: RuleVersion): RuleGroupType {
    const resourceGroupType = readGroupTypeFromRecord(v.resource?.group);
    if (resourceGroupType) {
        return resourceGroupType;
    }

    const snapshots = getSnapshots(v);
    const preferredSnapshot =
        v.changeType === AuditLogChangeType.DELETE
            ? (snapshots.before ?? snapshots.after)
            : (snapshots.after ?? snapshots.before);
    const snapshotGroupType = readGroupTypeFromRecord(preferredSnapshot?.group);
    if (snapshotGroupType) {
        return snapshotGroupType;
    }

    const fromResourceType = resourceTypeToRuleGroupType(v.resourceType);
    if (fromResourceType) {
        return fromResourceType;
    }

    return RuleGroupType.TRANSACTION;
}

function buildUpdateRow(v: RuleVersion): RuleDetailsRowData {
    const snaps = getSnapshots(v);

    const beforeData = extractRuleDataFromSnapshot(snaps.before);
    const afterData = extractRuleDataFromSnapshot(snaps.after, v.resource);
    const merged = mergeSnapshotData(beforeData, afterData);

    const hasPerFieldConditions =
        v.diffs.some((d) => isConditionField(d.field)) ||
        snapshotHasFieldKeys(snaps.before, snaps.after, isConditionField);
    const hasPerFieldActions =
        v.diffs.some((d) => isActionField(d.field)) ||
        snapshotHasFieldKeys(snaps.before, snaps.after, isActionField);

    const conditionFieldDiffs = hasPerFieldConditions
        ? collectAllFieldItems({
              snaps,
              diffs: v.diffs,
              predicate: isConditionField,
          })
        : [];
    const actionFieldDiffs = hasPerFieldActions
        ? collectAllFieldItems({
              snaps,
              diffs: v.diffs,
              predicate: isActionField,
          })
        : [];

    let conditions: TransactionRuleCondition[] = [];
    let conditionTexts: string[] = [];
    let prevConditionTexts: string[] = [];
    let actions: TransactionRuleAction[] = [];
    let actionTexts: string[] = [];
    let prevActionTexts: string[] = [];

    if (!hasPerFieldConditions) {
        const resolved = resolveUpdateDisplayConditions(v);
        conditions = resolved.conditions;
        conditionTexts = resolved.conditionTexts;
        prevConditionTexts = resolved.prevConditionTexts;
        if (conditions.length === 0 && conditionTexts.length === 0) {
            conditions = afterData.conditions;
        }
    }

    if (!hasPerFieldActions) {
        const resolved = resolveUpdateDisplayActions(v);
        actions = resolved.actions;
        actionTexts = resolved.actionTexts;
        prevActionTexts = resolved.prevActionTexts;
        if (actions.length === 0 && actionTexts.length === 0) {
            actions = afterData.actions;
        }
    }

    const rawCondDiff = getDiff(v.diffs, "rawConditions");
    const rawActDiff = getDiff(v.diffs, "rawActions");

    let conditionSegments: Array<
        RuleItemSegment<TransactionRuleCondition>
    > | null = null;
    if (rawCondDiff && conditions.length > 0) {
        const beforeList = resolveBeforeRawConditions(v.diffs, snaps);
        conditionSegments = buildRuleItemSegments(
            beforeList,
            conditions,
            isEqualCondition,
        );
    }

    let actionSegments: Array<RuleItemSegment<TransactionRuleAction>> | null =
        null;
    if (rawActDiff && actions.length > 0) {
        const beforeList = resolveBeforeRawActions(v.diffs, snaps);
        actionSegments = buildRuleItemSegments(
            beforeList,
            actions,
            isEqualAction,
        );
    }

    const groupDiff = getDiff(v.diffs, "groupId");
    const groupColumn: GroupColumn = groupDiff
        ? {
              kind: "diff",
              beforeLabel: refLabel(groupDiff.before),
              afterLabel: refLabel(groupDiff.after),
          }
        : { kind: "name", label: resolveRuleGroupName(v) };

    return {
        id: v.versionId,
        versionId: v.versionId,
        ruleId: resolveRuleId(v),
        changeType: v.changeType,
        ruleType: resolveRuleType(v),
        groupColumn,
        statusColumn: resolveRuleStatusColumn(v),
        logDate: v.logDate,
        conditions,
        conditionTexts,
        actions,
        actionTexts,
        prevConditions: [],
        prevActions: [],
        prevConditionTexts,
        prevActionTexts,
        conditionSegments,
        actionSegments,
        conditionFieldDiffs,
        actionFieldDiffs,
        counterparties: merged.counterparties,
        normalizedDescriptions: merged.normalizedDescriptions,
        resource: v.resource,
    };
}

function buildCreateDeleteRow(v: RuleVersion): RuleDetailsRowData {
    const snaps = getSnapshots(v);
    const snapshot =
        v.changeType === AuditLogChangeType.DELETE
            ? (snaps.before ?? snaps.after)
            : (snaps.after ?? snaps.before);
    const data = extractRuleDataFromSnapshot(snapshot, v.resource);
    const diffSnapshot = buildPerFieldSnapshotFromDiffs(
        v.diffs,
        v.changeType === AuditLogChangeType.DELETE ? "before" : "after",
    );
    const fallbackData = extractRuleDataFromSnapshot(diffSnapshot, v.resource);
    const displayConditions =
        data.conditions.length > 0 ? data.conditions : fallbackData.conditions;
    const displayActions =
        data.actions.length > 0 ? data.actions : fallbackData.actions;
    const merged = mergeSnapshotData(data, fallbackData);

    const groupColumn: GroupColumn = {
        kind: "name",
        label: resolveRuleGroupName(v),
    };

    return {
        id: v.versionId,
        versionId: v.versionId,
        ruleId: resolveRuleId(v),
        changeType: v.changeType,
        ruleType: resolveRuleType(v),
        groupColumn,
        statusColumn: resolveRuleStatusColumn(v),
        logDate: v.logDate,
        conditions: displayConditions,
        conditionTexts: [],
        actions: displayActions,
        actionTexts: [],
        prevConditions: [],
        prevActions: [],
        prevConditionTexts: [],
        prevActionTexts: [],
        conditionSegments: null,
        actionSegments: null,
        conditionFieldDiffs: [],
        actionFieldDiffs: [],
        counterparties: merged.counterparties,
        normalizedDescriptions: merged.normalizedDescriptions,
        resource: v.resource,
    };
}

export function buildRuleRowData(v: RuleVersion): RuleDetailsRowData {
    if (v.changeType === AuditLogChangeType.UPDATE) {
        return buildUpdateRow(v);
    }
    return buildCreateDeleteRow(v);
}
